import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom"; // Import useParams
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faQuestionCircle,
  faArrowLeft,
  faEdit, // Using faEdit for the edit page title
} from "@fortawesome/free-solid-svg-icons";
import { baseUrl } from "../../config/baseUrl";
import { toast } from "react-toastify";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

export default function EditParagraph() {
  const { id } = useParams(); // Get the paragraph ID from the URL
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true); // New state for initial data fetch
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  const [errors, setErrors] = useState({});
  const [languageList, setLanguageList] = useState([]);
  const [sections, setSections] = useState([]);

  // Form state for Passage (Paragraph)
  const [formData, setFormData] = useState({
    title: "",
    status: "active",
    language: "",
    subject_id: "",
    topic_id: "",
    subtopic_id: "",
    section_id: "",
  });

  // --- Initial Data Fetch (Runs once) ---
  useEffect(() => {
    fetchSubjects();
    fetchLanguageList();
    fetchSections();
    if (id) {
      fetchParagraphData(id);
    } else {
      setInitialLoading(false);
    }
  }, [id]);

  const fetchParagraphData = async (paragraphId) => {
    try {
      const response = await fetch(`${baseUrl}/passage/${paragraphId}`, {
        headers: { Authorization: localStorage.getItem("token") },
      });
      const data = await response.json();

      if (response.ok && data.status) {
        const paragraph = data.data;

        // Set form data with existing paragraph values
        setFormData({
          title: paragraph.title,
          status: paragraph.status,
          language: paragraph.language?._id || paragraph.language,
          subject_id: paragraph.subject_id?._id || paragraph.subject_id,
          topic_id: paragraph.topic_id?._id || paragraph.topic_id,
          subtopic_id: paragraph.subtopic_id?._id || paragraph.subtopic_id,
          section_id: paragraph.section_id?._id || paragraph.section_id,
        });

        // The dependent fetches will run due to the next useEffect hooks
      } else {
        toast.error(data.message || "Failed to fetch paragraph data");
        navigate("/paragraph"); // Redirect if fetch fails
      }
    } catch (error) {
      console.error("Error fetching paragraph data:", error);
      toast.error("Error fetching paragraph data");
      navigate("/paragraph"); // Redirect on network error
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchLanguageList = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/language/language-list?status=true`,
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        }
      );

      if (response.data.status) {
        setLanguageList(response.data.data);
      } else {
        toast.error("Failed to fetch language list");
      }
    } catch (error) {
      console.error("Error fetching language list:", error);
      toast.error("Error fetching language list");
    }
  };

  // --- Dependent Topic/Subtopic Fetches (On ID Change) ---
  useEffect(() => {
    if (formData.subject_id) {
      fetchTopics(formData.subject_id);
    } else {
      setTopics([]);
      // Do not reset subtopic_id here if initialLoading is true, let it stay for pre-fill
      if (!initialLoading) {
        setFormData((prev) => ({ ...prev, topic_id: "", subtopic_id: "" }));
      }
    }
  }, [formData.subject_id]);

  useEffect(() => {
    if (formData.topic_id) {
      fetchSubtopics(formData.topic_id);
    } else {
      setSubtopics([]);
      // Do not reset subtopic_id here if initialLoading is true, let it stay for pre-fill
      if (!initialLoading) {
        setFormData((prev) => ({ ...prev, subtopic_id: "" }));
      }
    }
  }, [formData.topic_id]);

  // --- Common Fetch Functions ---
  const fetchSubjects = async () => {
    try {
      const response = await fetch(`${baseUrl}/subjects`, {
        headers: { Authorization: localStorage.getItem("token") },
      });
      const data = await response.json();
      setSubjects(data.data || []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  const fetchTopics = async (subjectId) => {
    try {
      const response = await fetch(`${baseUrl}/topics?subject=${subjectId}`, {
        headers: { Authorization: localStorage.getItem("token") },
      });
      const data = await response.json();
      const filteredTopics = data.data.filter(
        (topic) => (topic.subject_id?._id || topic.subject_id) === subjectId
      );
      setTopics(filteredTopics);
    } catch (error) {
      console.error("Error fetching topics:", error);
    }
  };

  const fetchSubtopics = async (topicId) => {
    try {
      const response = await fetch(`${baseUrl}/subtopics?topic=${topicId}`, {
        headers: { Authorization: localStorage.getItem("token") },
      });
      const data = await response.json();
      const filteredSubtopics = data.data.filter(
        (subtopic) => (subtopic.topic_id?._id || subtopic.topic_id) === topicId
      );
      setSubtopics(filteredSubtopics);
    } catch (error) {
      console.error("Error fetching subtopics:", error);
    }
  };

  const fetchSections = async () => {
    try {
      const response = await fetch(`${baseUrl}/sections`, {
        headers: { Authorization: localStorage.getItem("token") },
      });
      const data = await response.json();
      setSections(data.data || []);
    } catch (error) {
      console.error("Error fetching sections:", error);
      setSections([]);
    }
  };

  // --- Input and Submit Handlers ---

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Reset dependent fields for selects
    if (name === "subject_id") {
      setFormData((prev) => ({ ...prev, topic_id: "", subtopic_id: "" }));
    } else if (name === "topic_id") {
      setFormData((prev) => ({ ...prev, subtopic_id: "" }));
    }
    // Note: Do not use setInitialLoading(false) here. It should only be set after the *initial* fetch is complete.
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      // Prepare data for update
      const passageData = {
        title: formData.title,
        status: formData.status,
        language: formData.language,
        subject_id: formData.subject_id,
        topic_id: formData.topic_id,
        subtopic_id: formData.subtopic_id,
        section_id: formData.section_id,
      };

      // Use PUT or PATCH for update
      const response = await fetch(`${baseUrl}/passage/${id}`, {
        method: "PUT", // Assuming your API uses PUT for full updates
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("token"),
        },
        body: JSON.stringify(passageData),
      });

      const result = await response.json();

      if (response.ok && result.status) {
        Swal.fire({
          title: "Success!",
          text: result.message || "Passage updated successfully",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        }).then(() => {
          navigate("/paragraph");
        });
      } else {
        throw new Error(result.message || "Failed to update passage");
      }
    } catch (error) {
      console.error("Error updating passage:", error);
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to update passage",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleQuillChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Show a loading indicator while fetching initial data
  if (initialLoading) {
    return (
      <section className="main-sec">
        <div className="container-fluid">
          <div className="text-center py-5">
            <h4>Loading Paragraph Data...</h4>
          </div>
        </div>
      </section>
    );
  }

  // --- Component Render ---
  return (
    <>
      <section className="main-sec">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-6">
              <div className="dashboard-title">
                <h4 className="dash-head">
                  <FontAwesomeIcon icon={faEdit} className="me-2" />
                  Edit Paragraph
                </h4>
              </div>
              <div className="custom-bredcump">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/">Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/paragraph">Passages</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Edit Paragraph
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
            <div className="col-lg-6 d-flex justify-content-end align-items-start">
              <Link
                to="/paragraph"
                className="btn-info text-white btn-add-passage me-2 bg-info"
              >
                View Passages
              </Link>
              <button onClick={handleCancel} className="btn-add-passage">
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12">
              <div className="cards">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    {/* Subject */}
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        Subject <span className="text-danger">*</span>
                      </label>
                      <select
                        name="subject_id"
                        className="form-select"
                        value={formData.subject_id}
                        onChange={handleInputChange}
                        required
                        disabled={initialLoading} // Disable while initial data is loading
                      >
                        <option value="">-- Select Subject --</option>
                        {subjects.map((subject) => (
                          <option key={subject._id} value={subject._id}>
                            {subject.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Topic */}
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        Topic <span className="text-danger">*</span>
                      </label>
                      <select
                        name="topic_id"
                        className="form-select"
                        value={formData.topic_id}
                        onChange={handleInputChange}
                        required
                        disabled={!formData.subject_id || initialLoading}
                      >
                        <option value="">-- Select Topic --</option>
                        {topics.map((topic) => (
                          <option key={topic._id} value={topic._id}>
                            {topic.topic_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Subtopic */}
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        Subtopic <span className="text-danger">*</span>
                      </label>
                      <select
                        name="subtopic_id"
                        className="form-select"
                        value={formData.subtopic_id}
                        onChange={handleInputChange}
                        required
                        disabled={!formData.topic_id || initialLoading}
                      >
                        <option value="">-- Select Subtopic --</option>
                        {subtopics.map((subtopic) => (
                          <option key={subtopic._id} value={subtopic._id}>
                            {subtopic.sub_topic_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Language */}
                    <div className="col-md-3 mb-3">
                      <label className="form-label">
                        Language <span className="text-danger">*</span>
                      </label>
                      <select
                        name="language"
                        className={`form-select ${
                          errors.language ? "is-invalid" : ""
                        }`}
                        value={formData.language}
                        onChange={handleInputChange}
                        required
                        disabled={initialLoading}
                      >
                        <option value="">-- Select language --</option>
                        {languageList.map((lang) => (
                          <option key={lang._id} value={lang._id}>
                            {lang.name}
                          </option>
                        ))}
                      </select>
                      {errors.language && (
                        <div className="invalid-feedback">
                          {errors.language}
                        </div>
                      )}
                    </div>

                    {/* Section */}
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        Section <span className="text-danger">*</span>
                      </label>
                      <select
                        name="section_id"
                        className={`form-select`}
                        value={formData.section_id}
                        onChange={handleInputChange}
                        required
                        disabled={initialLoading}
                      >
                        <option value="">-- Select Section --</option>
                        {sections.map((section) => (
                          <option key={section._id} value={section._id}>
                            {section.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Title */}
                    {/* <div className="col-md-12 mb-3">
                      <label className="form-label">
                        Title <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        className="form-control"
                        placeholder="Enter title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        disabled={initialLoading}
                      />
                    </div> */}
                    <div className="col-md-12 mb-3">
                      <label className="form-label">Title</label>
                      <ReactQuill
                        theme="snow"
                        value={formData.title}
                        onChange={(value) => handleQuillChange("title", value)}
                        modules={{
                          toolbar: [
                            [{ header: [1, 2, 3, 4, 5, 6, false] }],
                            ["bold", "italic", "underline", "strike"],
                            [{ list: "ordered" }, { list: "bullet" }],
                            [{ script: "sub" }, { script: "super" }],
                            [{ indent: "-1" }, { indent: "+1" }],
                            ["link", "image", "formula"],
                            ["clean"],
                          ],
                        }}
                        className="custom-quill-editor"
                      />
                    </div>

                    {/* Status */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Status</label>
                      <select
                        name="status"
                        className="form-select"
                        value={formData.status}
                        onChange={handleInputChange}
                        disabled={initialLoading}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>

                    {/* Submit Buttons */}
                    <div className="col-md-12">
                      <div className="d-flex gap-3">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading || initialLoading}
                        >
                          {loading ? "Updating..." : "Update Paragraph"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleCancel}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Re-use the existing CSS styles */}
      <style jsx>{`
        .cards {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          padding: 30px;
          margin-bottom: 30px;
        }

        .form-label {
          font-weight: 600;
          color: #333;
          margin-bottom: 8px;
        }

        .form-control,
        .form-select {
          border: 1px solid #ddd;
          border-radius: 6px;
          padding: 10px 12px;
          font-size: 14px;
          transition: border-color 0.3s ease;
        }

        .form-control:focus,
        .form-select:focus {
          border-color: #007bff;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }

        .btn-primary {
          background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
          border: none;
          padding: 12px 24px;
          font-weight: 600;
          border-radius: 6px;
          transition: all 0.3s ease;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
        }

        .btn-secondary {
          background: #6c757d;
          border: none;
          padding: 12px 24px;
          font-weight: 600;
          border-radius: 6px;
          color: white;
        }

        .text-danger {
          color: #dc3545 !important;
        }

        @media (max-width: 768px) {
          .cards {
            padding: 20px;
          }
        }
      `}</style>
    </>
  );
}
